import { filterBySearch } from "@/shared/utils/text-search";

/**
 * Agrupa as opções de uma caixa de escolha por GRUPO (hoje: tema →
 * grupo de tema) e aplica a busca, nas duas caixas que mostram tema
 * (`MultiSelectFilter` e `CatalogMultiSelect`). A lógica mora aqui, pura,
 * pra as duas não discordarem sobre ordem nem sobre o que a busca acha.
 *
 * - A busca casa o RÓTULO ou o NOME DO GRUPO, sem acento e sem caixa
 *   (`foldText`, o dono dessa comparação): digitar "pedagio" traz os
 *   temas do grupo PEDÁGIO inteiro, a mesma regra da aba Temas de
 *   Ajustes.
 * - Grupos em ordem alfabética pt-BR, opções em ordem alfabética dentro
 *   do grupo, e "sem grupo" (`group: null`) SEMPRE por último.
 * - Grupo sem opção depois da busca some (não vira título solto).
 * - **Nenhuma opção tem grupo → um bloco só, `group: null`, na ordem
 *   original**: é o que mantém as outras dimensões (polaridade, canal,
 *   marca…) com a cara de lista plana de sempre. Quem desenha decide
 *   pelo `isGrouped`, e não pelo formato do resultado: depois de uma
 *   busca, uma lista agrupada pode sobrar só com o bloco "sem grupo".
 */
export type OptionBlock<T> = { group: string | null; options: T[] };

export function isGrouped<T>(
  options: T[],
  groupOf: (option: T) => string | null | undefined
): boolean {
  return options.some(option => Boolean(groupOf(option)));
}

export function groupOptions<T>(
  options: T[],
  term: string,
  { labelOf, groupOf }: {
    labelOf: (option: T) => string;
    groupOf: (option: T) => string | null | undefined;
  }
): Array<OptionBlock<T>> {
  const matching = filterBySearch(options, term, option => [
    labelOf(option),
    groupOf(option)
  ]);
  if (!matching.length) return [];
  if (!isGrouped(options, groupOf)) return [{ group: null, options: matching }];

  const byGroup = new Map<string | null, T[]>();
  for (const option of matching) {
    const group = groupOf(option) || null;
    const bucket = byGroup.get(group);
    if (bucket) bucket.push(option);
    else byGroup.set(group, [option]);
  }
  const byLabel = (a: T, b: T) => labelOf(a).localeCompare(labelOf(b), "pt-BR");
  const named = [...byGroup.keys()]
    .filter((group): group is string => group !== null)
    .sort((a, b) => a.localeCompare(b, "pt-BR"))
    .map(group => ({ group, options: [...byGroup.get(group)!].sort(byLabel) }));
  const ungrouped = byGroup.get(null);
  return ungrouped
    ? [...named, { group: null, options: [...ungrouped].sort(byLabel) }]
    : named;
}
